import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

interface AccountPreferences {
  pinnedTabs?: string[];
  tourCompletedAt?: string | null;
  [key: string]: unknown;
}

// Per-user so the flag never leaks across accounts that share a browser. A
// global key would suppress the first-login tour for every subsequent account.
const LS_PREFIX = "dibs_tour_completed_at";

export function useOnboardingTour() {
  const { user, isStaff } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ["account_preferences", user?.id];
  const lsKey = user ? `${LS_PREFIX}_${user.id}` : LS_PREFIX;

  const { data: preferences, isLoading } = useQuery<AccountPreferences>({
    queryKey,
    queryFn: async () => {
      if (!user) return {};
      const { data, error } = await supabase
        .from("accounts")
        .select("preferences")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return (data?.preferences as AccountPreferences) ?? {};
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  });

  // Synchronous fast-path so we don't flash the tour while the DB read settles.
  const localCompletedAt =
    typeof window !== "undefined" ? window.localStorage.getItem(lsKey) : null;
  const completedAt = preferences?.tourCompletedAt ?? localCompletedAt ?? null;

  // Tour is offered to admins only on first login. Staff don't get it.
  const isFirstTime = !isLoading && !isStaff && !completedAt;

  const persist = async (value: string | null) => {
    if (!user) return;
    if (typeof window !== "undefined") {
      if (value) window.localStorage.setItem(lsKey, value);
      else window.localStorage.removeItem(lsKey);
    }

    const previous = queryClient.getQueryData<AccountPreferences>(queryKey);
    const next: AccountPreferences = { ...previous, tourCompletedAt: value };
    queryClient.setQueryData(queryKey, next);

    const { error } = await supabase
      .from("accounts")
      .update({ preferences: next as unknown as Json })
      .eq("user_id", user.id);

    if (error) {
      // Roll back local state if persistence fails. User will see the tour again next time.
      queryClient.setQueryData(queryKey, previous);
    }
  };

  const markComplete = () => persist(new Date().toISOString());

  return { isFirstTime, isLoading, markComplete };
}
